import { ref, computed, watch, onUnmounted, readonly } from 'vue'

export interface TimerOptions {
  /** Initial time in seconds */
  timeRemaining: number
  /** Whether to auto-start the timer */
  autoStart?: boolean
  /** Callback when timer reaches 0 */
  onTimeUp?: () => void
  /** Callback on each tick (optional) */
  onTick?: (timeLeft: number) => void
}

export function useTimer(options: TimerOptions) {
  const currentTime = ref(options.timeRemaining)
  const initialTime = ref(60) // Timer always starts from 60 seconds
  const isRunning = ref(false)
  const lastServerTime = ref(options.timeRemaining) // Track server updates
  let intervalId: number | null = null

  // Formatted time display
  const formattedTime = computed(() => {
    const totalSeconds = Math.floor(currentTime.value) // Always use whole seconds for display
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    return {
      minutes: minutes.toString().padStart(2, '0'),
      seconds: seconds.toString().padStart(2, '0'),
      display: `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
  })

  // Progress percentage (100% = full time, 0% = time up)
  const progress = computed(() => {
    if (!initialTime.value) return 0
    return Math.max(0, (currentTime.value / initialTime.value) * 100)
  })

  // Timer state checks
  const isTimeUp = computed(() => currentTime.value <= 0)
  const isLowTime = computed(() => currentTime.value <= 10 && currentTime.value > 0)

  const start = () => {
    if (intervalId) return // Already running

    isRunning.value = true
    intervalId = setInterval(() => {
      if (currentTime.value > 0) {
        currentTime.value = Math.max(0, currentTime.value - 0.1) // Update every 100ms for smoother animation
        options.onTick?.(Math.floor(currentTime.value))
      } else {
        stop()
        options.onTimeUp?.()
      }
    }, 100) // Update 10x per second for smooth visual progress
  }

  const stop = () => {
    if (intervalId) {
      clearInterval(intervalId)
      intervalId = null
    }
    isRunning.value = false
  }

  const pause = () => {
    stop()
  }

  const resume = () => {
    if (currentTime.value > 0) {
      start()
    }
  }

  const reset = (newTime?: number) => {
    stop()
    const timeToSet = newTime ?? 60
    currentTime.value = timeToSet
    // Keep initialTime fixed at 60 seconds
  }

  const setTime = (newTime: number) => {
    const wasRunning = isRunning.value
    stop()
    currentTime.value = newTime
    // Keep initialTime fixed at 60 seconds for consistent progress calculation

    if (wasRunning && newTime > 0) {
      start()
    }
  }

  // Watch for external timeRemaining changes
  watch(
    () => options.timeRemaining,
    newTime => {
      const timeDiff = Math.abs(newTime - currentTime.value)

      // Only reset timer if the new time is significantly different from current time
      if (timeDiff > 2) {
        setTime(newTime)
        lastServerTime.value = newTime
      } else if (newTime === 0 && currentTime.value > 0) {
        // Server says time is up, sync immediately to maintain game state consistency
        setTime(0)
        lastServerTime.value = newTime
      } else if (newTime > lastServerTime.value) {
        // Server time increased (new turn started), reset immediately
        setTime(newTime)
        lastServerTime.value = newTime
      }
      // Otherwise, let client timer continue running smoothly
    }
  )

  // Auto-start if requested
  if (options.autoStart && options.timeRemaining > 0) {
    start()
  }

  // Cleanup on unmount
  onUnmounted(() => {
    stop()
  })

  return {
    // State
    currentTime: readonly(currentTime),
    initialTime: readonly(initialTime),
    isRunning: readonly(isRunning),

    // Computed
    formattedTime,
    progress,
    isTimeUp,
    isLowTime,

    // Actions
    start,
    stop,
    pause,
    resume,
    reset,
    setTime
  }
}
